import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Box } from '@repo/ui/Box';
import { Icon } from '@repo/ui/Icon';
import { rawString, Text } from '../Language/Language';
import { useSafeWorkflow } from '../../workflow/hooks';
import s from './ConfirmationEmail.module.css';
import { Flex } from '@repo/ui/Flex';
import { Button } from '@repo/ui/Button';
import { Input } from '@repo/ui/Input';
import { InputField } from '@repo/ui/Input/InputField';
import { InputGroup } from '@repo/ui/Input/InputGroup';
import { InputLabel } from '@repo/ui/Input/InputLabel';
import { InputMessage } from '@repo/ui/Input/InputMessage';
import { InputRoot } from '@repo/ui/Input/InputRoot';

import { sendEmailMutationFn } from '../../graphql/mutation/sendEmail';
import { clientLogger } from '../../utils/logger';
import { useToast } from '@repo/ui/Toast/ToastRoot';
import { useSearch } from '@tanstack/react-router';
import type { OrderToken, Order } from '../../graphql/query/getOrder';

const EmailFormSchema = z.object({
  email: z
    .string()
    .min(1, {
      message: rawString({
        langKey: 'Global.confirmation.email.resend.requiredError',
      }),
    })
    .email({
      message: rawString({
        langKey: 'Global.confirmation.email.resend.validationError',
      }),
    }),
});

export type EmailFormSchemaType = z.infer<typeof EmailFormSchema>;

export type ConfirmationEmailProps = {
  email?: string;
  requestId?: string;
};

export const ConfirmationEmail = (props: ConfirmationEmailProps) => {
  const { requestId, email } = props;
  const [isInputOpen, setIsInputOpen] = useState<boolean>(false);
  const workflow = useSafeWorkflow();
  const params = useSearch({ strict: false });
  const { errorToast } = useToast();
  const methods = useForm<EmailFormSchemaType>({
    resolver: zodResolver(EmailFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  });
  const {
    register,
    formState,
    handleSubmit: handleEmailSubmit,
    watch,
  } = methods;
  const { errors, isValid } = formState;
  let order: Order | null | undefined = workflow?.order;
  if (!order && params?.order) {
    try {
      order = JSON.parse(params.order);
    } catch {
      order = undefined;
    }
  }
  const tokens: (OrderToken | null)[] = order?.orderTokens ?? [];
  const emailToken = tokens.find(
    (k): k is OrderToken => !!k && k.key === 'EMAIL',
  );
  const recipientEmail = emailToken?.value || email;
  const {
    mutate: sendEmail,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: async (data: EmailFormSchemaType) => {
      if (!data.email || !requestId || !order) return;
      return sendEmailMutationFn({ requestId, email: data.email });
    },
    onError: (error) => {
      errorToast({
        message:
          rawString({
            langKey: 'Global.confirmation.email.resend.submitError',
          }) ?? '',
      });
      clientLogger.logError(error);
    },
  });
  if (!order) return null;

  const handleClick = () => setIsInputOpen(!isInputOpen);

  const handleSubmit = handleEmailSubmit((data, e) => {
    e?.preventDefault();
    sendEmail(data);
  });

  return (
    <FormProvider {...methods}>
      <Flex className={s.wrapper} direction="column" gap={['gx-16', 'gy-8']}>
        <Box className={s.icon}>
          <Icon size="lg" name={'email'} />
        </Box>
        <div className={s.title}>
          {recipientEmail ? (
            <>
              <div>
                <Text
                  langKey="Global.confirmation.email.title"
                  replacementTokens={{ email: recipientEmail }}
                />
              </div>
              <p className={s.content}>
                <Text
                  langKey="Global.confirmation.email"
                  replacementTokens={{ email: recipientEmail }}
                />
              </p>
            </>
          ) : (
            <div>
              <Text langKey="Global.confirmation.email.title.fallback" />
            </div>
          )}
        </div>

        <Flex direction="column" gap="gy-8" className={s.link}>
          {isSuccess ? (
            <Text
              langKey="Global.confirmation.email.resend.success"
              replacementTokens={{ email: watch('email') }}
            />
          ) : (
            <>
              <Button variant="link" onClick={handleClick} className={s.button}>
                <Text langKey="Global.confirmation.email.resend.button" />
              </Button>
              {requestId && isInputOpen && (
                <InputRoot width="full">
                  <InputGroup>
                    <InputField>
                      <InputLabel htmlFor="email">
                        <Text langKey="Global.confirmation.email.resend.placeholder" />
                      </InputLabel>
                      <Input
                        id="email"
                        {...register('email')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSubmit(e);
                          }
                        }}
                      />
                    </InputField>
                    <Button
                      isLoading={isPending}
                      ariaDisabled={!isValid}
                      onClick={handleSubmit}
                    >
                      Submit
                    </Button>
                  </InputGroup>
                  {errors.email && (
                    <InputMessage variant="error">
                      <Icon name="alert" size="font" />
                      {errors.email.message}
                    </InputMessage>
                  )}
                </InputRoot>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </FormProvider>
  );
};
